import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { DestroyRef, Directive, inject, input } from '@angular/core';
import { RESEND_INVITATION_CONFIRMATION_MODAL_DATA } from '@shared/directives/invitations/constants/invitation-modals-data.constants';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { ToastService } from '@shared/components/toast';
import { catchError, filter, finalize, of, switchMap, tap } from 'rxjs';
import {
  InvitationResourceService,
  InvitationHttpService,
  LoaderService,
} from '@shared/services';
import { INVITATION_ACTION_TOASTS } from './constants/invitation-actions-toasts.constant';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Directive({
  selector: 'icon-button[appResendInvitation]',
  host: {
    '(click)': 'openConfirmationModal()',
  },
})
export class ResendInvitationDirective {
  public invitationId = input.required<number>();
  private readonly _invitationResourceService = inject(
    InvitationResourceService,
  );
  private readonly _invitationHttpService = inject(InvitationHttpService);
  private readonly _toast = inject(ToastService);
  private readonly _loaderService = inject(LoaderService);
  private readonly _dialog = inject(Dialog);
  private readonly _destroyRef = inject(DestroyRef);

  private _dialogRef?: DialogRef<unknown, ConfirmationModalComponent>;

  protected openConfirmationModal() {
    this._dialogRef = this._dialog.open(ConfirmationModalComponent, {
      data: RESEND_INVITATION_CONFIRMATION_MODAL_DATA,
    });

    this._observeDialogClose();
  }

  private _observeDialogClose() {
    this._dialogRef?.closed
      .pipe(
        filter((confirmed) => confirmed === true),
        tap(() => this._loaderService.show()),
        switchMap(() => this._resendInvitation()),
        catchError(() => {
          this._toast.show(INVITATION_ACTION_TOASTS['resendError']);
          return of(null);
        }),
        tap(() => {
          this._toast.show(INVITATION_ACTION_TOASTS['resendSuccess']);
          this._invitationResourceService.resetResource();
        }),
        finalize(() => this._loaderService.hide()),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  private _resendInvitation() {
    return this._invitationHttpService.resendInvitation(this.invitationId());
  }
}
